import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { mockDocuments } from '../../data/mockData';

const KYC_TYPES = new Set(['Aadhaar Card', 'PAN Card', 'Passport', 'Voter ID', 'Driving License']);
const INCOME_TYPES = new Set(['Salary Slip', 'Bank Statement', 'ITR', 'Form 16']);

const chip = (text) => (
  <Badge variant="outline" className="bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
    {text}
  </Badge>
);

export default function EmployeeDocuments() {
  const location = useLocation();

  const mode = (() => {
    if (location.pathname.includes('/employee/documents/income')) return 'income';
    if (location.pathname.includes('/employee/documents/rejected')) return 'rejected';
    if (location.pathname.includes('/employee/documents/verified')) return 'verified';
    return 'kyc';
  })();

  const docs = useMemo(() => {
    if (mode === 'rejected') return mockDocuments.filter((d) => d.status === 'Rejected');
    if (mode === 'verified') return mockDocuments.filter((d) => d.status === 'Verified');
    if (mode === 'income') return mockDocuments.filter((d) => INCOME_TYPES.has(d.docType) && d.status !== 'Verified');
    return mockDocuments.filter((d) => KYC_TYPES.has(d.docType) && d.status !== 'Verified');
  }, [mode]);

  const title =
    mode === 'income'
      ? 'Income Pending'
      : mode === 'rejected'
      ? 'Rejected Documents'
      : mode === 'verified'
      ? 'Verified Documents'
      : 'KYC Pending';

  const description =
    mode === 'verified'
      ? 'Verified documents and audit details'
      : 'Documents requiring review to proceed with loan processing';

  return (
    <div className="space-y-6" data-testid="employee-documents">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">{title}</h1>
          <p className="text-muted-foreground mt-1">{description}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button asChild size="sm" variant={mode === 'kyc' ? 'default' : 'outline'}>
            <Link to="/employee/documents/kyc">KYC Pending</Link>
          </Button>
          <Button asChild size="sm" variant={mode === 'income' ? 'default' : 'outline'}>
            <Link to="/employee/documents/income">Income Pending</Link>
          </Button>
          <Button asChild size="sm" variant={mode === 'rejected' ? 'default' : 'outline'}>
            <Link to="/employee/documents/rejected">Rejected Docs</Link>
          </Button>
          <Button asChild size="sm" variant={mode === 'verified' ? 'default' : 'outline'}>
            <Link to="/employee/documents/verified">Verified Docs</Link>
          </Button>
        </div>
      </div>

      <Card className="border border-border">
        <CardHeader>
          <CardTitle>Documents</CardTitle>
          <CardDescription>Review status by customer/lead</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Lead</TableHead>
                <TableHead>Document</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Upload Date</TableHead>
                <TableHead>Verified By</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {docs.map((d) => (
                <TableRow key={d.id}>
                  <TableCell className="font-medium">
                    <div className="flex flex-col">
                      <span>{d.leadName}</span>
                      <span className="text-xs text-muted-foreground font-mono">{d.leadId}</span>
                    </div>
                  </TableCell>
                  <TableCell>{d.docType}</TableCell>
                  <TableCell>{chip(d.status)}</TableCell>
                  <TableCell>{d.uploadDate}</TableCell>
                  <TableCell>{d.verifiedBy ? chip(d.verifiedBy) : <span className="text-muted-foreground">—</span>}</TableCell>
                </TableRow>
              ))}
              {docs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-10 text-muted-foreground">
                    No documents found for this view.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
